import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Plus, Pencil, Power } from '../lib/icons';
import { PageTitle } from '../components/shared/badges';
import { fetchCategoriesDepenses, enregistrerCategorieDepense } from '../lib/categoriesDepenses';

const inputCls = 'mt-1.5 h-11 w-full border border-slate-300 bg-slate-50 px-3 text-sm text-slate-900 focus:border-slate-900';
const vide = { libelle: '', groupe: 'Général', ordre: 100, usages: ['depenses', 'frais-generaux'], actif: true };
const USAGES = { depenses: 'Dépenses, caisse et scan', 'frais-generaux': 'Frais généraux' };

export default function CategoriesDepenses() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [erreur, setErreur] = useState('');
  const charger = useCallback(async () => {
    try { setRows(await fetchCategoriesDepenses({ toutes: true })); setErreur(''); }
    catch (e) { setErreur(e.message); }
  }, []);
  useEffect(() => { charger(); }, [charger]);

  const sauver = async (fiche) => {
    if (busy) return;
    setBusy(true);
    try {
      await enregistrerCategorieDepense(fiche);
      toast.success('Catégorie enregistrée');
      setForm(null);
      await charger();
    } catch (e) { toast.error(e.message); await charger(); }
    finally { setBusy(false); }
  };

  return <div className="space-y-6" data-testid="categories-depenses-page">
    <PageTitle emoji="🏷️" titre="Catégories de dépenses"
      sous="Classez vos catégories et choisissez celles proposées lors de la saisie."
      action={<button type="button" onClick={() => setForm({ ...vide })} className="flex h-11 items-center gap-2 bg-slate-900 px-4 text-sm font-bold text-white"><Plus size={16} /> Nouvelle catégorie</button>} />
    <p className="border border-slate-200 bg-white p-4 text-sm text-slate-600">
      Renommer ou désactiver une catégorie conserve les opérations déjà enregistrées.
    </p>
    {erreur && <div role="alert" className="bg-red-50 p-4 text-red-800">{erreur} <button type="button" onClick={charger} className="underline">Réessayer</button></div>}
    {form && <form onSubmit={(e) => { e.preventDefault(); sauver(form); }} className="space-y-4 border border-slate-200 bg-white p-5" data-testid="categorie-form">
      <h2 className="font-bold">{form.id ? 'Modifier la catégorie' : 'Nouvelle catégorie'}</h2>
      <div className="grid gap-4 sm:grid-cols-3">
        <label className="text-sm">Libellé
          <input required maxLength={100} disabled={form.protegee} className={inputCls} value={form.libelle} onChange={(e) => setForm({ ...form, libelle: e.target.value })} />
        </label>
        <label className="text-sm">Groupe
          <input required maxLength={80} list="groupes-categories" className={inputCls} value={form.groupe} onChange={(e) => setForm({ ...form, groupe: e.target.value })} />
          <datalist id="groupes-categories">{[...new Set(rows.map((c) => c.groupe))].map((g) => <option key={g} value={g} />)}</datalist>
        </label>
        <label className="text-sm">Ordre d’affichage
          <input type="number" required min={0} max={10000} step={1} className={inputCls} value={form.ordre} onChange={(e) => setForm({ ...form, ordre: e.target.value })} />
        </label>
      </div>
      <fieldset className="flex flex-wrap gap-4"><legend className="mb-2 text-sm font-bold">Disponible dans</legend>
        {Object.entries(USAGES).map(([key, label]) => <label key={key} className="flex items-center gap-2 text-sm">
          <input type="checkbox" disabled={form.protegee} checked={form.usages.includes(key)} onChange={(e) => setForm({ ...form, usages: e.target.checked ? [...form.usages, key] : form.usages.filter((u) => u !== key) })} />{label}
        </label>)}
      </fieldset>
      {form.protegee && <p className="text-sm text-amber-800">Le libellé et les usages de cette catégorie sont liés à une règle métier et restent protégés.</p>}
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.actif} onChange={(e) => setForm({ ...form, actif: e.target.checked })} /> Catégorie active</label>
      <div className="flex gap-3">
        <button disabled={busy || !form.usages.length} className="h-11 bg-slate-900 px-5 text-white disabled:opacity-50" type="submit">{busy ? 'Enregistrement…' : 'Enregistrer'}</button>
        <button disabled={busy} className="h-11 border border-slate-300 px-5" type="button" onClick={() => setForm(null)}>Annuler</button>
      </div>
    </form>}
    <div className="overflow-x-auto border border-slate-200 bg-white">
      <table className="w-full text-left text-sm"><thead className="bg-slate-50 text-slate-500"><tr>
        {['Catégorie', 'Groupe', 'Ordre', 'Utilisation', 'Statut', 'Actions'].map((h) => <th key={h} className="p-3">{h}</th>)}
      </tr></thead><tbody>
        {rows.map((c) => <tr key={c.id} className="border-t border-slate-100">
          <td className="p-3 font-semibold">{c.libelle}</td><td className="p-3">{c.groupe}</td><td className="p-3">{c.ordre}</td>
          <td className="p-3">{c.usages.map((u) => USAGES[u]).join(' · ')}</td>
          <td className="p-3">{c.actif ? 'Active' : 'Désactivée'}</td>
          <td className="p-3"><div className="flex gap-2">
            <button disabled={busy} type="button" aria-label={`Modifier ${c.libelle}`} onClick={() => setForm({ ...c })} className="border border-slate-300 p-2"><Pencil size={16} /></button>
            <button disabled={busy} type="button" aria-label={`${c.actif ? 'Désactiver' : 'Activer'} ${c.libelle}`} onClick={() => sauver({ ...c, actif: !c.actif })} className="border border-slate-300 p-2"><Power size={16} /></button>
          </div></td>
        </tr>)}
        {!rows.length && !erreur && <tr><td colSpan={6} className="p-4 text-slate-500">Aucune catégorie.</td></tr>}
      </tbody></table>
    </div>
  </div>;
}
